import moment from 'moment'
import AuthMixin from '@/components/mixins/auth-mixin'
import AdminUserInvite from './admin-user-invite/admin-user-invite.vue'
import EventBus from '@/components/mixins/event-bus-mixin'
import TagSlider from '@/components/common/tag-slider/tag-slider-tmpl'
import AdminHeader from './admin-header.vue'
import CONST from '@/globals/constants'
import Draggable from 'vuedraggable'
import { tagsStore } from '../../../pinia/stores/tagsStore'
import { mapState } from 'pinia'

import { USER_RIGHTS } from '@base-shared/utils/constants'

export default {
  name: 'adminPage',
  inject: [
    '$currentUser',
    '$signedInUserInfo',
    '$utils',
    '$api',
    '$apiError'
  ],
  components: {
    AdminUserInvite,
    TagSlider,
    AdminHeader,
    Draggable
  },
  props: {
    dataProp: {
      type: String,
      default () { return '' }
    }
  },
  computed: {
    hubUserManagementLink () {
      return CONFIG?.HAS_HUB ? `https://${CONFIG?.FRONTEND_HUB_DOMAIN}/settings/user-management` : ''
    },
    sortDraggableOptions () {
      return { animation: 250 }
    },
    ...mapState(tagsStore, ['tagSliderTagCombinations']),
    storeOnLoadTagsFromSlider () {
      const onLoadTagSlider = this.tagSliderTagCombinations
      let res = onLoadTagSlider

      // Make sure it's a flat array
      if ((typeof onLoadTagSlider === 'object' && !Array.isArray(onLoadTagSlider) && onLoadTagSlider !== null)) {
        res = []
        for (const tagType in onLoadTagSlider) {
          for (const arrIndx in onLoadTagSlider[tagType]) {
            const tag = onLoadTagSlider[tagType][arrIndx]
            tag.type = tagType
            res.push(tag)
          }
        }
      }

      return res
    },
    loggedInUserId () {
      const userDetails = this.$currentUser.getUserDetails();
      return userDetails?.preferred_username ?? userDetails?.sub;
    },
    filteredUserList () {
      if (!this.searchKey) return this.listOfUsers

      return this.listOfUsers.filter((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()

        const email = user.email.toLowerCase()

        const searchKeyLower = this.searchKey.toLowerCase()

        return fullName.includes(searchKeyLower) || email.includes(searchKeyLower)
      })
    }
  },
  watch: {
    storeOnLoadTagsFromSlider: {
      handler () {
        this.listOfUsers = this.filterListOfUsersData(this.listOfUsers)
      },
      deep: true
    }
  },
  mixins: [AuthMixin],
  data () {
    return {
      CONST,
      USER_RIGHTS,
      tooltip: {
        [USER_RIGHTS.ARYA_ADMIN]: this.$t('label.admin'),
        [USER_RIGHTS.ARYA_BILLING]: this.$t('label.billing'),
        [USER_RIGHTS.ARYA_ALERT]: this.$t('label.alert', 2),
        [USER_RIGHTS.ARYA_PLAYER]: this.$t('label.player', 2),
        [USER_RIGHTS.ARYA_SCHEDULE]: this.$t('label.schedule'),
        [USER_RIGHTS.ARYA_CREATE]: this.$t('label.create'),
        [USER_RIGHTS.ARYA_CREATE_LOCK]: this.$t('label.modify'),
        [USER_RIGHTS.ARYA_LIMITED]: this.$t('label.limited')
      },
      allowableRightsOptions: {
        [USER_RIGHTS.ARYA_ADMIN]: USER_RIGHTS.ARYA_ADMIN,
        [USER_RIGHTS.ARYA_BILLING]: USER_RIGHTS.ARYA_BILLING,
        [USER_RIGHTS.ARYA_ALERT]: USER_RIGHTS.ARYA_ALERT,
        [USER_RIGHTS.ARYA_PLAYER]: USER_RIGHTS.ARYA_PLAYER,
        [USER_RIGHTS.ARYA_SCHEDULE]: USER_RIGHTS.ARYA_SCHEDULE,
        [USER_RIGHTS.ARYA_CREATE]: USER_RIGHTS.ARYA_CREATE,
        [USER_RIGHTS.ARYA_CREATE_LOCK]: USER_RIGHTS.ARYA_CREATE_LOCK,
        [USER_RIGHTS.ARYA_LIMITED]: USER_RIGHTS.ARYA_LIMITED
      },
      listOfUsers: [],
      showUserForm: false,
      userGroupColor: {
        [USER_RIGHTS.ARYA_ADMIN]: '#f1606d',
        [USER_RIGHTS.ARYA_BILLING]: '#f18c60',
        [USER_RIGHTS.ARYA_CREATE]: '#52f9e5',
        [USER_RIGHTS.ARYA_CREATE_LOCK]: '#52f9e5',
        [USER_RIGHTS.ARYA_SCHEDULE]: '#4bccdb',
        [USER_RIGHTS.ARYA_PLAYER]: '#628bc8',
        [USER_RIGHTS.ARYA_ALERT]: '#7471c5'
      },
      disabledIcons: [],
      showRowByTags: [],
      refreshTagSlider: { ref: null, refresh: false },
      userSelection: [],
      showTagsSlider: (
        this.$currentUser.hasGroupTagsUser() || this.$currentUser.hasGroupDevelopment()
      ) && !this.$signedInUserInfo?.activePlan?.suspended,
      searchKey: ''
    }
  },
  methods: {
    getUsersList (params) {
      if (!params) params = {}

      const getUsersListRequest = () => {
        this.$utils.mainLoader(true)

        this.$api.get(`${CONFIG.API_BASE_URL}/group/users`)
          .then(response => {
            const responseData = response.data

            // Validate the static value of allowable rights.
            for (const indx in this.allowableRightsOptions) {
              const currentRightItem = this.allowableRightsOptions[indx]
              if (responseData.allowableRights.indexOf(currentRightItem) < 0) {
                delete this.allowableRightsOptions[indx]
              }
            }

            // Sort users list first
            const sortedList = this.sortListOfUsersData(responseData.users)

            this.listOfUsers = this.filterListOfUsersData(sortedList)
            this.$nextTick(_ => {
              this.listOfUsers.forEach(user => { this.adjustUserRowTagsWrapperStyle({ userId: user.userId }) })
              this.$utils.updatePageComponentStyle({ name: 'adminPage' })
            })

            if (params.callback) {
              params.callback()
            } else {
              this.$utils.mainLoader(false)
            }
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { getUsersListRequest() } })
          })
      }
      getUsersListRequest()
    },
    showUserRightOption (right, user) {
      const defaultRights = !user.tags || !user.tags.length
      const withUserTagsRights = user.tags && user.tags.length
      let res = true

      // User with no tags
      if (defaultRights) {
        const disabledRights = [this.allowableRightsOptions[USER_RIGHTS.ARYA_LIMITED]]

        res = true
        if (disabledRights.indexOf(right) >= 0) {
          res = false
        }

        // Just show global admin right even if user has both global admin and arya admin rights
        if (this.userHasGlobalAdminRights(user) && right === USER_RIGHTS.ARYA_ADMIN) {
          res = false
        }
      }

      // User with tags
      if (withUserTagsRights) {
        const enabledRights = [this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE_LOCK], this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE], this.allowableRightsOptions[USER_RIGHTS.ARYA_SCHEDULE]]

        if (this.$currentUser.hasGroupDevelopment() || user.groupId === '7acOurk9lQN3TncCgBu85vRqmpkduFkYRaNUKmiRtk') {
          enabledRights.push(this.allowableRightsOptions[USER_RIGHTS.ARYA_LIMITED])
        }
        // Default user rights for user tags
        res = false
        if (enabledRights.indexOf(right) >= 0) {
          res = true
        }

        // Hide user_create and user_schedule if user has user_limited rights
        const hasUserLimitedAndCreateLock = (user.rights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_LIMITED]) >= 0) && (user.rights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE_LOCK]) >= 0)
        const hideRightsIfUserLimited = [this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE], this.allowableRightsOptions[USER_RIGHTS.ARYA_SCHEDULE]]
        if (hasUserLimitedAndCreateLock && (hideRightsIfUserLimited.indexOf(right) >= 0)) {
          res = false
        }

        // Hide user_limited if user has user_create or user_schedule rights
        const hasUserCreateORSchedule = (user.rights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE]) >= 0) || (user.rights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_SCHEDULE]) >= 0)
        if (hasUserCreateORSchedule && (right === this.allowableRightsOptions[USER_RIGHTS.ARYA_LIMITED])) {
          res = false
        }
      }

      return res
    },
    /**
     * Handle user click on any element inside user row
     */
    handleUserRowClickCapture (event, user, indx) {
      const targetDOM = event.target

      // Tag Item action
      if ($(targetDOM).hasClass('user_right_action_icon') || $(targetDOM).hasClass('remove-user-tag')) return false

      // Extend row to show all tags
      if ($(targetDOM).hasClass('show-all-tags')) {
        this.adjustUserRowStyle({ extendRow: true, userId: user.userId })
        return false
      }

      if (!$(targetDOM).hasClass('user-tag-name')) {
        const isSelected = this.userSelection.find((u) => { return u.userId === user.userId })
        if (!isSelected) {
          this.userSelection = [user]
        } else {
          this.userSelection = []
        }

        this.resetAllUserRowStyle()
        this.listOfUsers = this.filterListOfUsersData(this.listOfUsers)
      }
      this.$nextTick(_ => { this.$utils.updatePageComponentStyle({ name: 'adminPage' }) })
    },
    userHasGlobalAdminRights (userSrc) {
      return userSrc.rights.indexOf(USER_RIGHTS.GLOBAL_ADMIN) >= 0
    },
    userHasAdminRights (userSrc) {
      return userSrc.rights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_ADMIN]) >= 0
    },
    userHasEnterpriseRights (userSrc) {
      return userSrc.rights.indexOf('enterprise') >= 0
    },
    /**
     * User Rights icon selection
     * @param {String} userRight - selected user rights
     * @param {Object} user - user details
     */
    handleUserRightsIconClick (userRight, user) {
      const selectedUser = this.$utils.clone(user)
      let newUserRights = selectedUser.rights

      // If user has enterprise rights then do not add billing rights
      if (this.userHasEnterpriseRights(selectedUser) && userRight === this.allowableRightsOptions[USER_RIGHTS.ARYA_BILLING]) {
        return
      }

      // Check if user has admin or global admin rights
      const hasAdminRights = this.userHasAdminRights(selectedUser) || this.userHasGlobalAdminRights(selectedUser)

      // If user has admin rights then deny other user rights selection
      if (hasAdminRights && userRight !== this.allowableRightsOptions[USER_RIGHTS.ARYA_ADMIN]) {
        return
      }

      // Admin should not be able to remove their OWN admin rights
      if (hasAdminRights && selectedUser._edit.disabledRemoveOwnUserAdmin) {
        return
      }

      // If selected user rights is not exist in current user rights list then add it, else remove from the list
      const selectedUserRightPositionInCurrentUserRights = newUserRights.indexOf(userRight)
      if (selectedUserRightPositionInCurrentUserRights < 0) {
        newUserRights.push(userRight)
      } else {
        newUserRights.splice(selectedUserRightPositionInCurrentUserRights, 1)

        // When removing admin rights, create, schedule and player should be enabled by default
        if (userRight === this.allowableRightsOptions[USER_RIGHTS.ARYA_ADMIN]) {
          newUserRights = [
            this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE],
            this.allowableRightsOptions[USER_RIGHTS.ARYA_SCHEDULE],
            this.allowableRightsOptions[USER_RIGHTS.ARYA_PLAYER]
          ]
        }
      }

      // Cleanup, If new rights list has admin then it should be user_admin only
      if (newUserRights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_ADMIN]) >= 0) {
        newUserRights = [this.allowableRightsOptions[USER_RIGHTS.ARYA_ADMIN]]
      }

      /* Adjust user rights lock_create and create
        - If the user click on the lock-create, the create icon is disabled
I       - If the user click on the create, the lock-create icon is disabled
      */
      const findUserCreateLockRight = newUserRights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE_LOCK])
      const findUserCreateRight = newUserRights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE])

      if (userRight === this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE_LOCK] && findUserCreateLockRight >= 0) {
        if (findUserCreateRight >= 0) {
          newUserRights.splice(findUserCreateRight, 1)
        }
      }
      if (userRight === this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE] && findUserCreateRight >= 0) {
        if (findUserCreateLockRight >= 0) {
          newUserRights.splice(findUserCreateLockRight, 1)
        }
      }

      /**
        * When user select 'user_limited', autmatically select also 'user_create_lock', and hide the modify, edit and publish icon
        * When user de-select limited, show the 3 other icons
        * When user select edit or publish, hide the limited icon
        * Icon visibility handled by showUserRightOption function
       */
      const userLimitedExist = newUserRights.find((u) => { return u === this.allowableRightsOptions[USER_RIGHTS.ARYA_LIMITED] })
      if (userLimitedExist) {
        // Add user_create_lock if not present
        if (newUserRights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE_LOCK]) < 0) {
          newUserRights.push(this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE_LOCK])
        }

        // Remove user_create if present
        if (newUserRights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE]) >= 0) {
          newUserRights.splice(newUserRights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE]), 1)
        }

        if (userRight === this.allowableRightsOptions[USER_RIGHTS.ARYA_CREATE_LOCK]) {
          return
        }
      }

      // Create user rights payload and set value based from new user rights
      const origAllowableRights = this.$utils.clone(this.allowableRightsOptions)
      const userRightsPayload = {}

      for (const indx in origAllowableRights) {
        const allowableRight = origAllowableRights[indx]

        userRightsPayload[allowableRight] = false

        if (newUserRights.indexOf(allowableRight) >= 0) {
          userRightsPayload[allowableRight] = true
        }
      }

      // Send request to backend
      const updateUserRightsRequest = () => {
        this.$utils.mainLoader(true)
        this.showUserForm = false

        this.$api.put(`${CONFIG.API_BASE_URL}/group/users/${selectedUser.userId}`, userRightsPayload)
          .then(response => {
            selectedUser.rights = newUserRights

            // Update list
            this.getUsersList({
              callback: () => {
                this.$nextTick(_ => { this.adjustUserRowTagsWrapperStyle({ userId: selectedUser.userId }) })
                this.$utils.mainLoader(false)
              }
            })
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { updateUserRightsRequest() }, showDialogNotification: true })
          })
      }
      updateUserRightsRequest()
    },
    /**
     * Sort users list by admin role (self first, then global admin, then admin, then others)
     * @param {Array} listOfUsers
     * @returns {Array} - sorted list of users
     */
    sortListOfUsersData (listOfUsers) {
      if (!listOfUsers?.length) return listOfUsers

      const getSortByPriority = (user) => {
        if (this.loggedInUserId === user.userId) return 0
        if (user.rights?.includes(USER_RIGHTS.GLOBAL_ADMIN)) return 1
        if (user.rights?.includes(USER_RIGHTS.ARYA_ADMIN)) return 2
        return 3
      }

      return [...listOfUsers].sort((a, b) => getSortByPriority(a) - getSortByPriority(b))
    },
    /**
     * Filter users list data to add some properties to be use in front-end
     * @param {Array} listOfUsers
     */
    filterListOfUsersData (listOfUsers) {
      const allowableRightsOptions = this.$utils.clone(this.allowableRightsOptions)

      for (const indx in listOfUsers) {
        const user = listOfUsers[indx]

        // Add _edit property if not present
        if (!user._edit) {
          user._edit = {
            isLoggedInUser: false,
            isUserCreateLockRightDisabled: false,
            isUserCreateRightDisabled: false,
            isUserRowSelected: false,
            mouseEvent: null,
            rowBackgroundStyle: '',
            disableUserRights: [],
            disabledRemoveOwnUserAdmin: false,
            disabledDeleteUser: false,
            showUserRow: true,
            lastLogin: 'none',

            // Tag global admin user
            isUserGlobalAdmin: false
          }
        }

        // Set if selected
        user._edit.isUserRowSelected = !!this.userSelection.find((selectedUser) => { return selectedUser.userId === user.userId })

        // Set last login
        if (user.lastSeenDevice && user.lastSeenDevice.lastSeen) {
          const getLastLogin = moment(user.lastSeenDevice.lastSeen)
          const translateMonth = this.$t('el.datepicker.months.' + getLastLogin.clone().format('MMM').toLowerCase())
          user._edit.lastLogin = `${getLastLogin.clone().format('DD')} ${translateMonth} ${getLastLogin.clone().format('YYYY')} ${getLastLogin.clone().format('hh:mm A')}`
        }

        const userHasAdminRight = this.userHasAdminRights(user)
        const userHasGlobalAdmin = this.userHasGlobalAdminRights(user)
        user._edit.isUserGlobalAdmin = userHasGlobalAdmin

        // User that has ARYA_ADMIN right or ACCOUNT_ADMIN right, we will hide all allowableRightOptions, except for ARYA_ADMIN
        if (userHasAdminRight || userHasGlobalAdmin) {
          for (const indx in allowableRightsOptions) {
            user._edit.disableUserRights.push(allowableRightsOptions[indx])
          }

          // Logged in user should not be able to remove their OWN admin rights and own account
          const loggedInUserId = this.$currentUser.getUserDetails()?.preferred_username ?? this.$currentUser.getUserDetails().sub
          if (user.userId === loggedInUserId) {
            user._edit.isLoggedInUser = true
            user._edit.disabledRemoveOwnUserAdmin = true
            user._edit.disabledDeleteUser = true
          } else {
            user._edit.disableUserRights = user._edit.disableUserRights.filter((userRight) => { return userRight !== USER_RIGHTS.ARYA_ADMIN })
          }
        }

        // Disabled user_billing rights if enterprise user
        if (this.$currentUser.hasGroupEnterprise()) {
          user._edit.disableUserRights.push(this.allowableRightsOptions[USER_RIGHTS.ARYA_BILLING])
        }

        /* ADD BACKGROUND STYLE */

        // User has no right then add single color
        if (user.rights.length === 0) {
          user._edit.rowBackgroundStyle = { backgroundColor: '#cccccc' }
        }

        // User has multiple rights
        if (user.rights.length >= 1) {
          const userHasBillingRight = user.rights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_BILLING]) >= 0

          // Force to add single color because user has billing right
          if (userHasBillingRight) {
            user._edit.rowBackgroundStyle = { backgroundColor: this.userGroupColor[this.allowableRightsOptions[USER_RIGHTS.ARYA_BILLING]] }
          }

          // Force to add single color because user has admin right
          if (userHasAdminRight || userHasGlobalAdmin) {
            user._edit.rowBackgroundStyle = { backgroundColor: this.userGroupColor[this.allowableRightsOptions[USER_RIGHTS.ARYA_ADMIN]] }
          }

          // User has no billing and admin rights
          if (!userHasBillingRight && !userHasAdminRight && !userHasGlobalAdmin) {
            const collatedColors = []

            // Collect all user rights colors
            for (const indx in user.rights) {
              const getUserRightColor = this.userGroupColor[user.rights[indx]]

              if (typeof (getUserRightColor) !== 'undefined') {
                collatedColors.push(getUserRightColor)
              }
            }

            // Create Gray Color
            if (collatedColors.length === 0) {
              user._edit.rowBackgroundStyle = { backgroundColor: '#cccccc' }
            }

            // Create Single color
            if (collatedColors.length === 1) {
              user._edit.rowBackgroundStyle = { backgroundColor: collatedColors[0] }
            }

            // Create Gradient
            if (collatedColors.length > 1) {
              let colors = ''
              const backgroundImageStyle = []
              const browserPrefixes = ['-o-', '-ms-', '-moz-', '-webkit-']

              // Collect all user rights colors
              for (const cIndx in collatedColors) {
                if (cIndx > 0) {
                  colors += `, ${collatedColors[cIndx]}`
                } else {
                  colors += `${collatedColors[cIndx]}`
                }
              }

              // Create backgroundImage style with browser prefix
              for (let i = 0; i < browserPrefixes.length; i++) {
                const backgroundImageVal = `${browserPrefixes[i]}linear-gradient(70deg, ${colors})`
                backgroundImageStyle.push({ backgroundImage: backgroundImageVal })
              }

              user._edit.rowBackgroundStyle = backgroundImageStyle
            }
          }
        }

        // Drag and drop status
        // user_schedule, user_create_lock
        const disabledAddTagsHasRights = [USER_RIGHTS.ARYA_ADMIN, USER_RIGHTS.ARYA_PLAYER, USER_RIGHTS.ARYA_ALERT, USER_RIGHTS.ARYA_BILLING]
        const hasRightsToDisableAddUserTag = user.rights.some((val) => disabledAddTagsHasRights.indexOf(val) !== -1)
        user._edit.enableAddUserTags = !hasRightsToDisableAddUserTag

        // User Tags
        if (user.tags) {
          const userTags = this.converUserTags({ convertTo: 'UIFormat', tags: user.tags })

          // Set tags color from tags slider
          userTags.forEach((userTag, indx) => {
            const detailsFromSlider = this.storeOnLoadTagsFromSlider.find((t) => { return t.value === userTag.value })
            if (detailsFromSlider && detailsFromSlider.color) {
              userTags[indx].backgroundColor = this.$utils.handleColorCodeConversion(detailsFromSlider.color, 'hex', 'rgba', 0.8)
            }
          })

          user.tags = userTags
        }

        // Row visibility
        if (this.showRowByTags.length) {
          const currentUserTags = this.converUserTags({ convertTo: 'backendFormat', tags: user.tags })
          const hasTagSameFromFilter = currentUserTags.some((val) => this.showRowByTags.indexOf(val) !== -1)

          user._edit.showUserRow = false
          if (hasTagSameFromFilter) user._edit.showUserRow = true
        } else {
          user._edit.showUserRow = true
        }
      }

      return listOfUsers
    },
    /**
     * Handle User Row onHover or onMouseout event.This will update the usersList data to add mouseEvent flag
     * @param {String} mouseEvent
     * @param {Object} currentUser
     */
    handleUserRowItemMouseHoverAndMouseOut (mouseEvent, currentUser) {
      const listOfUsers = this.$utils.clone(this.listOfUsers)

      for (const indx in listOfUsers) {
        const userItem = listOfUsers[indx]

        userItem._edit.mouseEvent = null

        if (!userItem._edit.isUserRowSelected) {
          userItem._edit.selectedMediaAction = null
        }

        if (userItem.userId === currentUser.userId) {
          listOfUsers[indx]._edit.mouseEvent = mouseEvent

          break
        }
      }

      this.listOfUsers = listOfUsers
    },
    /**
     * Handle delete icon action.
     * If hub feature flag is enabled, show remove arya access modal.
     * If hub feature flag is disabled, show delete user modal.
     * @param {Object} user
     */
    handleDelete (user) {
      if (this.$utils.hasHub()) {
        this.handleRemoveAryaAccess(user)
      } else {
        this.handleDeleteUser(user)
      }
    },
    /**
     * Handle delete user with confirmation box
     * @param {Object} user
     */
    handleDeleteUser (user) {
      if (user._edit.disabledDeleteUser) return

      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'warning',
        description: this.$t('label.removeUserFromAccount'),
        callbacks: {
          eventOnClose: () => { },
          eventOKButtonClick: () => {
            const deleteUserRequest = () => {
              this.$utils.mainLoader(true)
              this.$api.delete(`${CONFIG.API_BASE_URL}/group/users/${user.userId}`)
                .then(response => {
                  // NOTE: instead of re-loading list, just purge record from the local instance
                  this.listOfUsers = this.listOfUsers.filter((u) => { return u.userId !== user.userId })
                })
                .catch((error) => {
                  this.$apiError.handleAPIRequestError(error, { originalRequest: () => { deleteUserRequest() }, showDialogNotification: true })
                })
                .finally(() => this.$utils.mainLoader(false))
            }
            deleteUserRequest()
          }
        }
      })
    },
    handleRemoveAryaAccess (user) {
      if (user._edit.disabledDeleteUser) return

      const hasGroupGlobalAdmin = this.$currentUser.hasGroupGlobalAdmin()

      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'warning',
        title: this.$t('confirm.removeAccessFromArya'),
        descriptionI18n: {
          key: 'texts.removeAccessFromAryaMessage',
          params: { userName: `${user.firstName} ${user.lastName}` }
        },
        alert: {
          message: hasGroupGlobalAdmin
            ? this.$t('texts.removeAccessFromAryaByGlobalAdmin', { link: this.$t('texts.hubUserManagement') })
            : this.$t('texts.removeAccessFromAryaByNonGlobalAdmin')
        },
        alertMessageI18n: hasGroupGlobalAdmin
          ? {
              key: 'texts.removeAccessFromAryaByGlobalAdmin',
              params: {
                link: {
                  type: 'link',
                  text: this.$t('texts.hubUserManagement'),
                  href: this.hubUserManagementLink,
                  className: 'text-orange-800'
                }
              }
            }
          : null,
        confirmButtonText: this.$t('buttons.removeUser'),
        confirmButtonIcon: 'flowbite:trash-bin-solid',
        callbacks: {
          eventOnClose: () => { },
          eventOKButtonClick: () => {
            this.$utils.mainLoader(true)
            this.$api.patch(`${CONFIG.API_BASE_URL}/group/users/${user.userId}/products`, {
              remove: [CONST.PRODUCT_ARYA]
            })
              .then(response => {
                this.listOfUsers = this.listOfUsers.filter((u) => { return u.userId !== user.userId })
              })
              .catch((error) => {
                this.$apiError.handleAPIRequestError(error, {
                  originalRequest: () => { this.handleRemoveAryaAccess(user) },
                  showDialogNotification: true
                })
              })
              .finally(() => {
                this.$utils.mainLoader(false)
              })
          }
        }
      })
    },
    /**
     * Hook function on AdminUserInvite component change
     */
    handleAdminUserInviteChanged (event) {
      if (event.action === 'NEW_USER_CREATED') {
        // Keep updated userInfo data that was bind in vue.
        this.$utils.mainLoader(true)
        this.getUserUserInfoFromBackend({
          callback: () => {
            this.getUsersList()
          }
        })
      }
    },
    handleTagSliderChange (event) {
      if (event.action === CONST.EMIT_EVENT_NEW_SELECTED_TAGS_FROM_SLIDER) {
        this.showRowByTags = event.payload.data
        this.listOfUsers = this.filterListOfUsersData(this.listOfUsers)
        this.$nextTick(_ => {
          // Adjust tags scrollbar
          this.listOfUsers.forEach((user) => {
            if ((user.tags && user.tags.length) && event.payload.data.length) {
              const userIncludeTags = event.payload.data.filter(e => user.tags.indexOf(e) !== -1)

              if (userIncludeTags) {
                this.$nextTick(_ => { this.adjustUserRowTagsWrapperStyle({ userId: user.userId }) })
              }
            }
          })
        })
      }

      if (event.action === CONST.EMIT_EVENT_ALL_TAG_SELECTED_FROM_SLIDER) {
        this.showRowByTags = []
        this.getUsersList()
      }
    },
    converUserTags (params) {
      if (!params) { params = {} }

      if (params.convertTo === 'UIFormat') {
        const tags = []
        params.tags.forEach((tag, indx) => {
          if (typeof tag === 'string') {
            const newTag = {
              value: tag,
              backgroundColor: this.$utils.getBackgroundColor({ indx }),
              id: `${this.$utils.generateUniqueID()}${tags.length + 1}`
            }
            tags.push(newTag)
          } else {
            tags.push(tag)
          }
        })
        return tags
      }

      if (params.convertTo === 'backendFormat') {
        const tags = []
        params.tags.forEach((tag, indx) => {
          if (typeof tag === 'string') {
            tags.push(tag)
          } else {
            tags.push(tag.value)
          }
        })
        return tags
      }

      return params.tags
    },
    handleAssignTagToUser (params) {
      if (!params) { params = {} }

      const updateUserRequest = (reqParams) => {
        const requestData = { tags: reqParams.tags ? reqParams.tags : undefined }

        this.$api.put(`${CONFIG.API_BASE_URL}/group/users/${reqParams.userId}`, requestData)
          .then(response => {
            this.$utils.mainLoader(false)
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { updateUserRequest() }, showDialogNotification: true })
          })
      }

      const listOfUsers = this.$utils.clone(this.listOfUsers)
      const getUserDetails = listOfUsers.find((user) => { return user.userId === params.userId })

      if (getUserDetails && getUserDetails._edit.enableAddUserTags) {
        const tags = getUserDetails.tags ? getUserDetails.tags : []

        const isTagExist = tags.findIndex((t) => { return t.value === params.newTag }) >= 0

        if (!isTagExist) {
          tags.push(params.newTag)

          this.listOfUsers = this.filterListOfUsersData(listOfUsers) // Update current list
          const tagsBackendFormat = this.converUserTags({ convertTo: 'backendFormat', tags })
          updateUserRequest({ tags: tagsBackendFormat, userId: params.userId }) // Update backend
          this.$nextTick(_ => { this.adjustUserRowTagsWrapperStyle({ userId: params.userId, newAddedTag: true }) })
          if (params.showLoader) this.$utils.mainLoader(true)
        }
      }
    },
    adjustUserRowTagsWrapperStyle (params) {
      const userId = params.userId
      const targetRowEl = $('.users').find(`#${userId}`)
      const targetUserTagsWrapperEl = targetRowEl.find('.user-tags-wrapper')
      const isTargetRowHasPrevExtendableClass = targetUserTagsWrapperEl.hasClass('extendable')

      // Keep extended for delete tag
      if (params.deletedTag && (targetUserTagsWrapperEl.hasClass('extendable') && (targetUserTagsWrapperEl.hasClass('extended')))) {
        this.adjustUserRowStyle({ extendRow: true, userId })
        return
      }

      const getTargetUserTagsWrapperOffset = () => {
        const divWrapper = targetRowEl.find('.user-tags-wrapper')
        return {
          top: (divWrapper.offset() && divWrapper.offset().top) ? divWrapper.offset().top : 0,
          left: (divWrapper.offset() && divWrapper.offset().left) ? divWrapper.offset().left : 0,
          bottom: (divWrapper.offset() && divWrapper.offset().top) ? divWrapper.offset().top + divWrapper.height() : 0
        }
      }
      const getVisibleUserTagIds = () => {
        const visibleUserTagIds = []
        const divWrapper = targetRowEl.find('.user-tags-wrapper')
        const targetUserTagsWrapperOffset = getTargetUserTagsWrapperOffset()

        divWrapper.find('.user-tag').each((indx, el) => {
          const targetTagItemEl = $(el)
          const tagItemOffset = {
            top: (targetTagItemEl.offset() && targetTagItemEl.offset().top) ? targetTagItemEl.offset().top : 0,
            left: (targetTagItemEl.offset() && targetTagItemEl.offset().left) ? targetTagItemEl.offset().left : 0,
            bottom: (targetTagItemEl.offset() && targetTagItemEl.offset().top) ? targetTagItemEl.offset().top + targetTagItemEl.height() : 0
          }

          targetTagItemEl.removeClass('hide')
          targetTagItemEl.removeClass('show')
          targetTagItemEl.removeClass('hide-to-replace-by-show-more')

          // Check visible and none visible user tags
          if (!targetTagItemEl.hasClass('show-all-tags') && (tagItemOffset.top < targetUserTagsWrapperOffset.bottom && tagItemOffset.bottom < targetUserTagsWrapperOffset.bottom)) {
            visibleUserTagIds.push({ id: targetTagItemEl.attr('id'), name: targetTagItemEl.find('.user-tag-name').text() })
          } else {
            targetTagItemEl.addClass('hide')
          }
        })

        return visibleUserTagIds
      }
      const showMoreTagElementForExtendableRow = () => {
        const visibleUserTagIds = getVisibleUserTagIds()
        const divWrapper = targetRowEl.find('.user-tags-wrapper')

        // Hide the last visible user-tag element and show show-more-tag element next to it
        if (divWrapper.hasClass('extendable')) {
          const lastVisibleUserTagEl = visibleUserTagIds.length ? $(`#${visibleUserTagIds[visibleUserTagIds.length - 1].id}`) : null
          const lastVisibleShowMoreTagEl = visibleUserTagIds.length ? $(`#${visibleUserTagIds[visibleUserTagIds.length - 1].id}-show-all-tags`) : null

          if (lastVisibleUserTagEl) {
            lastVisibleUserTagEl.addClass('hide-to-replace-by-show-more')
            lastVisibleShowMoreTagEl.addClass('show')
          }
        }
      }

      const userTagsFlex = targetRowEl.find('.user-tags-flex').length ? targetRowEl.find('.user-tags-flex') : null
      const userTagsFlexHeight = userTagsFlex ? userTagsFlex.height() : 0
      targetUserTagsWrapperEl.scrollTop(0)

      // Add classname if extendable or fixed-center
      if (userTagsFlexHeight > targetUserTagsWrapperEl.height()) {
        targetRowEl.addClass('extendable')
        targetUserTagsWrapperEl.addClass('extendable')
        targetUserTagsWrapperEl.removeClass('fixed-center')
      } else {
        targetRowEl.removeClass('extendable')
        targetUserTagsWrapperEl.removeClass('extendable')
        targetUserTagsWrapperEl.removeClass('extended')
        targetUserTagsWrapperEl.addClass('fixed-center')
      }

      // Show more tag for extendable row
      showMoreTagElementForExtendableRow()

      // Extend row for if new added tag
      const keepExtended = targetUserTagsWrapperEl.hasClass('extendable') && params.newAddedTag
      if (keepExtended) this.adjustUserRowStyle({ extendRow: true, userId })

      // If deleted tags and has extendable classname previously
      if (params.deletedTag && isTargetRowHasPrevExtendableClass) {
        this.adjustUserRowTagsWrapperStyle({
          ...params,
          deletedTag: false
        })
      }
    },
    adjustUserRowStyle (params) {
      const userId = params.userId
      const targetRowEl = $(`#${userId}`)
      const rowHeightOrig = this.$utils.is1280View() ? 45 : 75

      if (params.extendRow) {
        targetRowEl.addClass('extended')
        targetRowEl.find('.user-tags-wrapper').addClass('extended')

        targetRowEl.find('.user-tag').each((indx, el) => {
          const userTagEl = $(el)

          if (userTagEl.hasClass('show-all-tags')) {
            userTagEl.removeClass('show')
          }

          userTagEl.removeClass('hide-to-replace-by-show-more')
          userTagEl.removeClass('hide')
        })

        // Adjust Row Height
        const userTagsWrapperEl = targetRowEl.find('.user-tags-flex')
        userTagsWrapperEl.addClass('extended')
        const userTagsWrapperElHeight = userTagsWrapperEl.height() + 15
        targetRowEl.css({ height: `${userTagsWrapperElHeight}px` })
      } else if (params.reset) {
        targetRowEl.removeClass('extendable')
        targetRowEl.removeClass('extended')
        targetRowEl.find('.user-tags-wrapper').removeClass('extendable')
        targetRowEl.find('.user-tags-wrapper').removeClass('extended')
        targetRowEl.css({ height: `${rowHeightOrig}px` })
      } else if (params.resetAllRows) {
        const targetRowEl = $(`#${userId}`)

        if (targetRowEl.hasClass('extended')) {
          targetRowEl.removeClass('extendable')
          targetRowEl.removeClass('extended')
          targetRowEl.find('.user-tags-wrapper').removeClass('extendable')
          targetRowEl.find('.user-tags-wrapper').removeClass('extended')
          targetRowEl.find('.user-tags-wrapper').removeClass('fixed-center')
          targetRowEl.find('.user-tags-flex').removeClass('extended')
          targetRowEl.css({ height: `${rowHeightOrig}px` })
          this.adjustUserRowTagsWrapperStyle({ userId })
        }
      }
    },
    resetAllUserRowStyle (params) {
      $('.users').find('.user-row').each((indx, el) => {
        const userId = $(el).attr('id')
        this.adjustUserRowStyle({ userId, resetAllRows: true })
      })
    },
    handleUserTagDeleteClick (user, userTag) {
      const updateUserRequest = (params) => {
        const requestData = { tags: params.tags ? params.tags : undefined }

        this.$api.put(`${CONFIG.API_BASE_URL}/group/users/${params.userId}`, requestData)
          .then(response => {
          })
          .catch((error) => {
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { updateUserRequest() }, showDialogNotification: true })
          })
      }

      const listOfUsers = this.$utils.clone(this.listOfUsers)
      const getUserIndx = listOfUsers.findIndex((u) => { return u.userId === user.userId })
      if (getUserIndx >= 0) {
        const userTagIndx = listOfUsers[getUserIndx].tags.findIndex((t) => { return t.id === userTag.id })

        if (userTagIndx >= 0) {
          listOfUsers[getUserIndx].tags.splice(userTagIndx, 1)
          const tagsBackendFormat = this.converUserTags({ convertTo: 'backendFormat', tags: listOfUsers[getUserIndx].tags })

          this.listOfUsers = this.filterListOfUsersData(listOfUsers)
          updateUserRequest({ tags: tagsBackendFormat, userId: user.userId }) // Update backend
          this.$nextTick(_ => { this.adjustUserRowTagsWrapperStyle({ userId: user.userId, deletedTag: true }) })

          // If empty tags and has user_limited right then remove user_limited right
          if (!listOfUsers[getUserIndx].tags.length && (user.rights && user.rights.indexOf(this.allowableRightsOptions[USER_RIGHTS.ARYA_LIMITED]) >= 0)) {
            this.handleUserRightsIconClick(this.allowableRightsOptions[USER_RIGHTS.ARYA_LIMITED], listOfUsers[getUserIndx])
          }
        }
      }
    },
    /**
     * Hook function for draggable as dropzone for `tags-shared` group
     *
     * @param {Object} event - VueDraggable change event
     * @param {Object} userItem - Player item
     */
    handleTagDraggableChange (event, userItem) {
      const tagData = event?.added?.element
      const userId = userItem?.userId

      if (!tagData?.value || !userId) {
        return
      }

      const newTag = tagData?.value
      const backgroundColor = this.$utils.rgbToHex(newTag?.backgroundColor)

      this.handleAssignTagToUser({ newTag, userId, backgroundColor, showLoader: false })
    },
    inviteUserClicked (showInviteUserForm) {
      this.showUserForm = showInviteUserForm
    }
  },
  mounted () {
    if (!this.$currentUser.hasGroupUserAdmin()) {
      this.$router.push('/menu')
    } else {
      // Fetch users list on scroll
      this.$utils.infiniteScroll('listOfUsersWrapper', () => {})

      $(window).on('resize', () => { this.$utils.updatePageComponentStyle({ name: 'adminPage' }) })

      // If Authenticated then get the users list
      this.isAuthenticated({
        callback: () => {
          this.getUsersList()
        }
      })
    }
  }
}
